// pages/public/ContactoPage.jsx
import React, { useEffect, useState } from 'react';
import PublicLayout from '../../layout/PublicLayout';
import { getRestauranteActual, getRedesSocialesPublicas } from '../../api/public';
import { useTypewriter } from '../../hooks/useTypewriter';
import { Mail, Phone, MapPin, Clock, Send, Facebook, Instagram, Twitter, Youtube, Linkedin, Globe } from 'lucide-react';

const ContactoPage = () => {
  const [restaurante, setRestaurante] = useState(null);
  const [redesSociales, setRedesSociales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  });
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });

  // Efecto typewriter para "Contáctanos"
  const typewriterText = useTypewriter('Contáctanos', 100, 50, 2000);

  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar restaurante
        const restauranteData = await getRestauranteActual();
        setRestaurante(restauranteData);

        // Cargar redes sociales
        const redesData = await getRedesSocialesPublicas();
        setRedesSociales(redesData);
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnviando(true);
    setMensaje({ tipo: '', texto: '' });

    // Validaciones
    if (!formData.nombre || !formData.correo || !formData.mensaje) {
      setMensaje({ tipo: 'error', texto: 'Por favor completa todos los campos obligatorios' });
      setEnviando(false);
      return;
    }

    // Simular envío (aquí deberías implementar el envío real)
    setTimeout(() => {
      setMensaje({ 
        tipo: 'success', 
        texto: '¡Mensaje enviado correctamente! Te contactaremos pronto.' 
      });
      setFormData({
        nombre: '',
        correo: '',
        telefono: '',
        asunto: '',
        mensaje: ''
      });
      setEnviando(false);
    }, 1500);
  };

  // Formatear horario
  const formatearHorario = (apertura, cierre) => {
    if (!apertura || !cierre) return 'Consultar horarios';
    return `${apertura.substring(0, 5)} - ${cierre.substring(0, 5)}`;
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-red-600 to-orange-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Mail className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              <span className="text-yellow-300">
                {typewriterText}
                <span className="animate-pulse">|</span>
              </span>
            </h1>
            <p className="text-xl text-red-50 max-w-2xl mx-auto">
              Estamos aquí para ayudarte. Envíanos tu mensaje y te responderemos pronto
            </p>
          </div>
        </div>
      </section>

      {/* Contenido Principal */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Formulario de Contacto */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Envíanos un <span className="text-red-600">Mensaje</span>
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nombre Completo <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Correo */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Correo Electrónico <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="correo"
                    value={formData.correo}
                    onChange={handleChange}
                    placeholder="tu@correo.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    placeholder="0999999999"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                {/* Asunto */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Asunto
                  </label>
                  <input
                    type="text"
                    name="asunto"
                    value={formData.asunto}
                    onChange={handleChange}
                    placeholder="¿En qué podemos ayudarte?"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                {/* Mensaje */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Mensaje <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    placeholder="Escribe tu mensaje aquí..."
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    required
                  ></textarea>
                </div>

                {/* Mensaje de respuesta */}
                {mensaje.texto && (
                  <div className={`p-4 rounded-lg ${
                    mensaje.tipo === 'success' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {mensaje.texto}
                  </div>
                )}

                {/* Botón */}
                <button
                  type="submit"
                  disabled={enviando}
                  className="w-full bg-red-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-red-700 transition-all shadow-lg hover:shadow-xl disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {enviando ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Enviar Mensaje
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Información de Contacto */}
            <div className="space-y-8">
              {/* Información del Restaurante */}
              {loading ? (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ) : restaurante && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    Información de <span className="text-red-600">Contacto</span>
                  </h3>

                  <div className="space-y-6">
                    {/* Dirección */}
                    {restaurante.direccion && (
                      <div className="flex items-start gap-4">
                        <div className="bg-red-100 p-3 rounded-full">
                          <MapPin className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Dirección</p>
                          <p className="text-gray-600">{restaurante.direccion}</p>
                        </div>
                      </div>
                    )}

                    {/* Teléfono */}
                    {restaurante.telefono && (
                      <div className="flex items-start gap-4">
                        <div className="bg-red-100 p-3 rounded-full">
                          <Phone className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Teléfono</p>
                          <a 
                            href={`tel:${restaurante.telefono}`}
                            className="text-red-600 hover:text-red-700 font-medium"
                          >
                            {restaurante.telefono}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Correo */}
                    {restaurante.correo && (
                      <div className="flex items-start gap-4">
                        <div className="bg-red-100 p-3 rounded-full">
                          <Mail className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Correo</p>
                          <a 
                            href={`mailto:${restaurante.correo}`}
                            className="text-red-600 hover:text-red-700 font-medium"
                          >
                            {restaurante.correo}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Horario */}
                    {(restaurante.horario_apertura && restaurante.horario_cierre) && (
                      <div className="flex items-start gap-4">
                        <div className="bg-red-100 p-3 rounded-full">
                          <Clock className="w-6 h-6 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 mb-1">Horario</p>
                          <p className="text-gray-600">
                            {formatearHorario(restaurante.horario_apertura, restaurante.horario_cierre)}
                          </p>
                          <p className="text-sm text-gray-500 mt-1">Lunes a Domingo</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Redes Sociales */}
              {redesSociales.length > 0 && (
                <div className="bg-gradient-to-r from-red-600 to-orange-500 rounded-2xl shadow-lg p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">
                    Síguenos en Redes Sociales
                  </h3>
                  <p className="text-red-50 mb-6">
                    Mantente al día con nuestras últimas ofertas y novedades
                  </p>
                  <div className="flex flex-wrap gap-4">
                    {redesSociales.map((red) => {
                      // Determinar el icono según el tipo
                      const getIcon = (tipo) => {
                        const tipoLower = tipo.toLowerCase();
                        if (tipoLower.includes('facebook')) return <Facebook className="w-6 h-6" />;
                        if (tipoLower.includes('instagram')) return <Instagram className="w-6 h-6" />;
                        if (tipoLower.includes('twitter') || tipoLower.includes('x')) return <Twitter className="w-6 h-6" />;
                        if (tipoLower.includes('youtube')) return <Youtube className="w-6 h-6" />;
                        if (tipoLower.includes('linkedin')) return <Linkedin className="w-6 h-6" />;
                        if (tipoLower.includes('tiktok')) return <span className="text-xl font-bold">TT</span>;
                        if (tipoLower.includes('whatsapp')) return <Phone className="w-6 h-6" />;
                        return <Globe className="w-6 h-6" />;
                      };

                      return (
                        <a
                          key={red.id_red}
                          href={red.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white bg-opacity-20 hover:bg-opacity-30 p-4 rounded-full transition-all group relative"
                          title={red.tipo}
                        >
                          {getIcon(red.tipo)}
                          {/* Tooltip */}
                          <span className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                            {red.tipo}
                          </span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ContactoPage;
